import React from 'react';
import { Typography } from '@attentive-platform/stem-ui';
import { COUNTRY, COUNTRY_CODE } from '../../Constants/Constants';
import { addOneYearToDate } from '../../Utils/HelperFunctions';
import ContractTermsSection from './ContractTermsSection';

export const BillingInfoSection = ({
  companyName,
  billingEmail,
  address,
  city,
  zipcode,
  country,
  handleDataChange,
  readOnly = false
}) => (
  <div className='border border-gray-100 rounded-lg p-4 mb-4 bg-gray-50'>
    <div className='flex items-center gap-1 mb-3 pb-2 border-b border-gray-100'>
      <Typography variant='subtitle1-medium' color='var(--color-neutral-80)'>
        Enterprise Billing Info
      </Typography>
      {readOnly && (
        <span className='ml-2 text-xs font-medium px-2 py-0.5 rounded bg-blue-100 text-blue-700'>
          Synced — read-only
        </span>
      )}
    </div>

    <div className='input-group mb-3'>
      <Typography className='input-group-text w-1/2'>Company Name</Typography>
      <input
        type='text'
        value={companyName}
        onChange={e => !readOnly && handleDataChange(e.target.value, 'company_name')}
        className='form-control '
        placeholder='Company Name'
        required
        disabled={readOnly}
      />
    </div>
    <div className='input-group mb-3 '>
      <Typography className='input-group-text w-1/2'>Company Billing Email</Typography>
      <input
        type='email'
        value={billingEmail}
        onChange={e => !readOnly && handleDataChange(e.target.value, 'billing_email')}
        className='form-control '
        placeholder='Email'
        required
        disabled={readOnly}
      />
    </div>
    <div className='input-group mb-3 '>
      <Typography className='input-group-text w-1/2'>Address</Typography>
      <input
        type='text'
        value={address}
        onChange={e => !readOnly && handleDataChange(e.target.value, 'address')}
        className='form-control '
        placeholder='Address'
        required
        disabled={readOnly}
      />
    </div>
    <div className='input-group mb-3 '>
      <Typography className='input-group-text w-1/2'>City</Typography>
      <input
        type='text'
        value={city}
        onChange={e => !readOnly && handleDataChange(e.target.value, 'city')}
        className='form-control '
        placeholder='City'
        required
        disabled={readOnly}
      />
    </div>

    <div className='input-group mb-3 '>
      <Typography className='input-group-text w-1/2'>Zipcode</Typography>
      <input
        type='text'
        value={zipcode}
        onChange={e => !readOnly && handleDataChange(e.target.value, 'zip')}
        className='form-control '
        placeholder='Zipcode'
        required
        disabled={readOnly}
      />
    </div>
    <div className='input-group mb-3 '>
      <Typography className='input-group-text w-1/2'>Country</Typography>
      <div className='dropdown w-1/2'>
        <button
          className='btn border text-dark dropdown-toggle w-full'
          type='button'
          data-toggle='dropdown'
          data-bs-toggle='dropdown'
          disabled={readOnly}>
          {COUNTRY[country]}
        </button>
        <div className='dropdown-menu w-full'>
          <span className='dropdown-item ' onClick={() => !readOnly && handleDataChange(COUNTRY_CODE.USA, 'country')}>
            United States
          </span>
          <span className='dropdown-item' onClick={() => !readOnly && handleDataChange(COUNTRY_CODE.CANADA, 'country')}>
            Canada
          </span>
          <span
            className='dropdown-item'
            onClick={() => !readOnly && handleDataChange(COUNTRY_CODE.AUSTRALIA, 'country')}>
            Australia
          </span>
        </div>
      </div>
    </div>
  </div>
);

/** Falcon enterprise conversion: billing + shared terms + aerial pricing (Beam uses dedicated Beam UI). */
export const EnterpriseBillingInfo = ({
  errorMessage,
  successMessage,
  isInvalidZip,
  contractInfo,
  setContractInfo,
  formData,
  setFormData,
  isBeam
}) => {
  const handleDataChange = (val, field) => {
    setContractInfo(prev => ({ ...prev, [field]: val }));
  };

  const setDates = date => {
    if (!date || String(date).length < 10) {
      setContractInfo(prev => ({ ...prev, start_date: date || '', end_date: '' }));
      return;
    }
    setContractInfo(prev => ({ ...prev, start_date: date, end_date: addOneYearToDate(date) }));
  };

  return (
    <div className='border p-4 w-full'>
      {(errorMessage || successMessage || isInvalidZip) && (
        <div className={`alert ${errorMessage || isInvalidZip ? 'alert-danger' : 'alert-success'}`} role='alert'>
          {errorMessage || successMessage || 'Please enter valid zip code'}
        </div>
      )}

      <BillingInfoSection
        city={contractInfo.city}
        companyName={contractInfo.company_name}
        billingEmail={contractInfo.billing_email}
        address={contractInfo.address}
        zipcode={contractInfo.zip}
        country={contractInfo.country}
        handleDataChange={handleDataChange}
      />

      <ContractTermsSection
        contractInfo={contractInfo}
        setContractInfo={setContractInfo}
        handleDataChange={handleDataChange}
        setDates={setDates}
        totalUsers={formData?.total_users ?? ''}
        onTotalUsersChange={val => setFormData(prev => ({ ...prev, total_users: val }))}
      />

      {!isBeam && (
        <>
          <div className='input-group mb-3 '>
            <Typography className='input-group-text w-1/2'>Aerial Contract Amount ($)</Typography>
            <input
              type='number'
              step={0.01}
              min={1}
              value={contractInfo.aerial_contract_amount}
              onChange={e => handleDataChange(e.target.value, 'aerial_contract_amount')}
              className='form-control '
              placeholder=''
              required
            />
          </div>
          <div className='input-group mb-3 '>
            <Typography className='input-group-text w-1/2'>Aerial Per Acre Cost ($)</Typography>
            <input
              type='number'
              step={0.01}
              min={1}
              value={contractInfo.per_acre_cost}
              onChange={e => handleDataChange(e.target.value, 'per_acre_cost')}
              className='form-control'
              required
            />
          </div>
          <div className='input-group mb-3 '>
            <Typography className='input-group-text w-1/2 text-wrap'>
              Per Acre Imagery Cost for Manual Measurement, Beyond Threshold Acres ($):
            </Typography>
            <input
              type='number'
              step={0.01}
              min={1}
              value={contractInfo.manual_per_acre_cost}
              onChange={e => handleDataChange(e.target.value, 'manual_per_acre_cost')}
              className='form-control'
              required
            />
          </div>
          <div className='input-group mb-3 '>
            <Typography className='input-group-text w-1/2 text-wrap'>
              Threshold Acres for Imagery cost in Manual Measurments ($):
            </Typography>
            <input
              type='number'
              step={0.01}
              min={1}
              defaultValue='20'
              value={contractInfo.manual_threshold_acres}
              onChange={e => handleDataChange(e.target.value, 'manual_threshold_acres')}
              className='form-control'
              required
            />
          </div>
          <div className='input-group mb-3 '>
            <Typography className='input-group-text w-1/2 text-wrap'>
              Aerial Remeasurement Contract Amount ($)
            </Typography>
            <input
              type='number'
              step={0.01}
              min={1}
              value={contractInfo.remeasurement_contract_amount}
              onChange={e => handleDataChange(e.target.value, 'remeasurement_contract_amount')}
              className='form-control '
              required
            />
          </div>
          <div className='input-group mb-3 '>
            <Typography className='input-group-text w-1/2 text-wrap'>Aerial Remeasurement Per Acre Cost ($)</Typography>
            <input
              type='number'
              step={0.01}
              min={1}
              value={contractInfo.remeasurement_per_acre_cost}
              onChange={e => handleDataChange(e.target.value, 'remeasurement_per_acre_cost')}
              className='form-control '
              required
            />
          </div>
        </>
      )}
    </div>
  );
};

export default BillingInfoSection;
